import { Injectable } from "@angular/core";

export interface TutionLocation {
  id: string;
  town: string;
  address: string;
  postcode: string;
  phone: string;
  email: string;
  image: string;
  badge?: string;
  distance?: number;
  services: string[];
  description: string;
  openingHours: { day: string; time: string }[];
  photos: string[];
  faqs: { question: string; answer: string }[];
  mapUrl: string;

  schedules?: CentreSchedule[];
}

export interface MockSchedule {
  centre: string;
  category: string;
  rows: { test: string; date: string }[];
}

export interface ScheduleRow {
  test: string;
  date: string;
  link?: string;
}

export interface CentreSchedule {
  title: string;
  rows: ScheduleRow[];
}

@Injectable({
  providedIn: "root",
})
export class GcseLocationService {
  private locations: TutionLocation[] = [
    //Chelmford
    {
      id: "essex-chelmsford-gcse-mocks",
      town: "Chelmsford",
      address:
        "The Boswells School, Burnham Rd, Springfield, Chelmsford CM1 6LY",
      postcode: "CM1 6LY",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://hcgroup.uk/wp-content/uploads/2021/09/cchsfg-sports-hall-and-teaching-block-097.jpg",
      badge: "Popular",
      distance: 2.5,
      services: [
        "11+ Mock Exams",
        "GCSE Mock Exams",
        "1-2-1 & Group Tuitions (11+, KS3, GCSE)",
      ],
      description:
        "Located in Chelmsford, this exam centre offers a quiet, structured environment ideal for focused assessment. Easy accessibility and a professional setting ensure a smooth and supportive exam experience for all students.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/chelmsford-1.jpg",
        "assets/images/chelmsford-2.jpg",
        "assets/images/chelmsford-3.jpg",
        "assets/images/chelmsford-4.jpg",
      ],

      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "Year 11 GCSE Mock Tests – Chelmsford",
          rows: [
            { test: "P-1 Maths (EDEXCEL)", date: "Sunday, January 11, 2026", link: "" },
            { test: "P-2 Maths (EDEXCEL)", date: "Sunday, January 18, 2026", link: "" },
            { test: "P-3 Maths (EDEXCEL)", date: "Sunday, January 25, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/e4d7232e9b59af81e534fc4feb30c93a" },

            {
              test: "P-1 Literature (AQA)",
              date: "Sunday, February 1, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/435b46c68e198369ce8b522b558d5ff8"
            },
            {
              test: "P-1 Literature (OCR)",
              date: "Sunday, February 1, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/2f88d4b519cc5b429fd3e1b9edf25405"
            },

            {
              test: "P-1 Literature (EDEXCEL)",
              date: "Sunday, February 1, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/7715d35d1512e8a6d828f0aeda1ab15f"
            },

            //2----------

            {
              test: "P-1 Language (AQA)",
              date: "Sunday, February 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/c3c06bec6c92171017aa7956e3ad659d"
            },

            {
              test: "P-1 Language (OCR)",
              date: "Sunday, February 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/61ac5548956d8a949b7a5b5dba126e0b"
            },

            {
              test: "P-1 Language (EDEXCEL)",
              date: "Sunday, February 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/c47947739477a8189f1ee26dede2fae9"
            },


            //3


            {
              test: "P-2 Literature (AQA)",
              date: "Sunday, February 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/b8d022f6601e00e2d01f8c7686ecdb27"
            },

             {
              test: "P-2 Literature (OCR)",
              date: "Sunday, February 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/225d8be5f86e0a35fa682c26d93d8b3b"
            },

             {
              test: "P-2 Literature (EDEXCEL)",
              date: "Sunday, February 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/f66eb87549255d53bdb9810392db9e84"
            },

            //4

            {
              test: "P-2 Language (AQA)",
              date: "Sunday, February 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/22f7e0fed48ee92403f5c6b5ddaa8136"
            },

            {
              test: "P-2 Language (OCR)",
              date: "Sunday, February 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/22f7e0fed48ee92403f5c6b5ddaa8136"
            },

            {
              test: "P-2 Language (EDEXCEL)",
              date: "Sunday, February 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/22f7e0fed48ee92403f5c6b5ddaa8136"
            },


            //5

            {
              test: "P-1 Physics (AQA)",
              date: "Sunday, March 1, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/bb6894eb3d9da8b6207a87e4051094ac"
            },

             {
              test: "P-1 Physics (OCR)",
              date: "Sunday, March 1, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/ea0bbd17e73671e850aad2859166e243"
            },

             {
              test: "P-1 Physics (EDEXCEL)",
              date: "Sunday, March 1, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/bb6894eb3d9da8b6207a87e4051094ac"
            },

            //6

            {
              test: "P-2 Physics (AQA)",
              date: "Sunday, March 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/d824d4d29aa6ad82d4971c24c92dd865"
            },

            {
              test: "P-2 Physics (OCR)",
              date: "Sunday, March 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/1c66381534831cb863d8b54aa1647ca0"
            },

            {
              test: "P-2 Physics (EDEXCEL)",
              date: "Sunday, March 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/5df3d93f2d3ea699e305f1aeb24e9974"
            },

            //7

            {
              test: "P-1 Chemistry (AQA)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/959f482eb0738ed9f404fd8a1f2a4600"
            },

            {
              test: "P-1 Chemistry (OCR)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/0a1c2c9c0a71faf95037dda1370889b6"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/09c151ad03497b45dbf36a1d33129685"
            },

            //8

            {
              test: "P-2 Chemistry (AQA)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/d22a6498c6d1a4038496f41b0fbaa68a"
            },

            {
              test: "P-2 Chemistry (OCR)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/c73ab40eeff57d3ada36570bf3d1c07c"
            },

            {
              test: "P-2 Chemistry (EDEXCEL)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/7a8a725fdfb6cb23385e580813c70eaf"
            },

            //9
            {
              test: "P-1 Biology (AQA)",
              date: "Sunday, March 29, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/2797fef56df0162353eff169ef23aebf"
            },

            {
              test: "P-1 Biology (OCR)",
              date: "Sunday, March 29, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/134259f0f6688f54a7a9792ad388acd5"
            },

            {
              test: "P-1 Biology (EDEXCEL)",
              date: "Sunday, March 29, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/5349fcccb7952f1c0975557e50acbaaf"
            },


            //10

            {
              test: "P-2 Biology (AQA)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/9ca5e06b4be180d1feb8d0fee576beb1"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/191191e47801fdde7258c310216bde00"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/3dd0e1c7fd34d1821debc2304c20123e"
            },

            //11

            {
              test: "P-1 Computer Science (AQA)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/c6677ad15c03d429d5e9b29f169269d5"
            },

            {
              test: "P-1 Computer Science (OCR)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/5a1df8d1cc35a3ccf0112b3e12f1ea2a"
            },

            {
              test: "P-1 Computer Science (EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/f8a4ba80dc370125caf2498ed3a5ed23"
            },

            //12

            {
              test: "P-2 Computer Science (AQA)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/4ccfde5933f55417b9511ddb43ab6573"
            },

            {
              test: "P-2 Computer Science (OCR)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/8547b848c9a7ff49e40a046953fb7c12"
            },

            {
              test: "P-2 Computer Science (EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/d6fc2c2989ab436ab3988cedaf40ed50"
            },
          ],
        },
         {
          title: "Year 10 GCSE Mock Tests – Chelmsford",
          rows: [

            //7

            {
              test: "P-1 Chemistry (AQA)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/59d798b451b7d964ba394d9eefd40ff1"
            },

            {
              test: "P-1 Chemistry (OCR)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/d2363f9fbbefd70c9ec1154ae1d64c04"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/2a35ab8e9e07499dc73a45c545f23102"
            },

            //8

            {
              test: "P-2 Chemistry (AQA)",
              date: "Saturday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/e1b39f6f3fe97966218b1790440eb5f7"
            },

            {
              test: "P-2 Chemistry (OCR)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/f8adc229c493917c37cf2265328c5c0a"
            },

            {
              test: "P-2 Chemistry (EDEXCEL)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/0ba8efffcab1e07e70845e80d86f6d30"
            },


            //10

            {
              test: "P-2 Biology (AQA)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/0ba8efffcab1e07e70845e80d86f6d30"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/8a4838c50208c6e16024e44da709a843"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/3f54d9bcaa90e68b929ac27508e29551"
            },

            //11

            {
              test: "P-1 Computer Science (AQA)",
              date: "Sunday, April 12, 2026", link: ""
            },

            {
              test: "P-1 Computer Science (OCR)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/fa7eca8aa47130874b491a2a313013e4"
            },

            {
              test: "P-1 Computer Science (EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/fa7eca8aa47130874b491a2a313013e4"
            },

            //12

            {
              test: "P-2 Computer Science (AQA)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/fcfa07bea4657f9cb8b2ccc5bc6157d8"
            },

            {
              test: "P-2 Computer Science (OCR)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/40e643bdfc5021a850a510433b65af9a"
            },

            {
              test: "P-2 Computer Science (EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/8a61cad5cd3201d7e643405a05dead72"
            },
          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d39458.14637312834!2d0.4485967!3d51.7356167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8c4e9c5e5e5e5%3A0x5c5e5e5e5e5e5e5!2sChelmsford%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //Ilford

    {
      id: "redbridge-ilford-gcse-mocks",
      town: "Ilford",
      address:
        "Oaks Park School (Main Gate), Block B, Near Sports complex, First Floor, Room C9",
      postcode: "IG2 7PR",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://www.rivingtonstreetstudio.co.uk/wordpress_2016/wp-content/uploads/RACHELFERRIMAN-RSS-LITTLE-ILFORD-LOWRES-034.jpg",
      distance: 5.3,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "Our Ilford exam centre is conveniently situated with excellent public transport links. It provides a calm, well-organised setting designed to support students of all abilities in achieving their best performance.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 7:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/ilford-1.jpg",
        "assets/images/ilford-2.jpg",
        "assets/images/ilford-3.jpg",
        "assets/images/ilford-4.jpg",
      ],

      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "Year 11 GCSE Mock Tests – Ilford",
          rows: [
            { test: "P-1 Maths (EDEXCEL)", date: "Sunday, January 11, 2026" },
            { test: "P-2 Maths (EDEXCEL)", date: "Sunday, January 18, 2026" },
            { test: "P-3 Maths (EDEXCEL)", date: "Sunday, January 25, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/79b82e7f27e95867e6ce980cccc9edf8"},


            //2

            {
              test: "P-1 Literature (AQA)",
              date: "Sunday, February 1, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/adc3d1bd093ead34989d82ae94ba1216"
            },

            {
              test: "P-1 Literature (OCR)",
              date: "Sunday, February 1, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/e049189e85fbd07d5d8d589224dce05d"
            },

            {
              test: "P-1 Literature (EDEXCEL)",
              date: "Sunday, February 1, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/86585d99f1ff2d992b6655e8319d562d"
            },

            // 3

            {
              test: "P-1 Language (AQA)",
              date: "Sunday, February 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/0f1b33dcc493cfa18f6988b3132c69fe"
            },

            {
              test: "P-1 Language (OCR)",
              date: "Sunday, February 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/8b55f7ab863846cf9195f073d211931a"
            },

            {
              test: "P-1 Language (EDEXCEL)",
              date: "Sunday, February 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/60d3a92416577e740dbe66569da06ba0"
            },

            

            //4

            {
              test: "P-2 Language (AQA)",
              date: "Sunday, February 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/91dec625b735547682779bb7e92e9941"
            },

            {
              test: "P-2 Language (OCR)",
              date: "Sunday, February 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/71e0fcc81b42732aad30ec88c782f7ed"
            },

            {
              test: "P-2 Language (EDEXCEL)",
              date: "Sunday, February 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/66e394cdfaa28092e732c7b920e48446"
            },

            //5
            {
              test: "P-1 Physics (AQA)",
              date: "Sunday, March 1, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/30d2d50b73e310f6459ff7c156a098dd"
            },

            {
              test: "P-1 Physics (OCR)",
              date: "Sunday, March 1, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/30d2d50b73e310f6459ff7c156a098dd"
            },

            {
              test: "P-1 Physics (EDEXCEL)",
              date: "Sunday, March 1, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/09ef1a78be9be491485343b412f8d352"
            },

            // 6

            {
              test: "P-2 Physics (AQA)",
              date: "Sunday, March 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/2f2c48de89d0baff47d5c947e270e3b4"
            },

            {
              test: "P-2 Physics (OCR)",
              date: "Sunday, March 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/b370633523de501f6b7a9380b6123982"
            },

            {
              test: "P-2 Physics (EDEXCEL)",
              date: "Sunday, March 8, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/65160746946722c0e6b6907a5d0bf9d5"
            },

            //7
            {
              test: "P-1 Chemistry (AQA)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/3394ba38982259f5f44086efc862abbd"
            },

            {
              test: "P-1 Chemistry (OCR)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/46a857febba236459fd6649a6a19d120"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/159b967c5b2086b316709ab369627f0e"
            },

            //8
            {
              test: "P-2 Chemistry (AQA)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/e05a2e48467cd65649deedfa85ee8e6a"
            },

            {
              test: "P-2 Chemistry (OCR)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/cba44ec95c925929ae953db68691f63b"
            },

            {
              test: "P-2 Chemistry (EDEXCEL)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/451f208bd9852ae0c316c0cd3301f03e"
            },
           
            
            //9
            {
              test: "P-2 Biology (AQA)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/d21c20f97e9f3afe1597fee5c5f1084c"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/1805297e61cc278e0490ef1f5502736e"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/57e9ab291b6d30100b1cb923c68084ca"
            },


            //10
            {
              test: "P-1 Computer Science (AQA)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/56d6c75bbc7c4678d7a9372f834f3ca8"
            },

            {
              test: "P-1 Computer Science (OCR)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/a12a8160701c7967d43b9411da8b53ee"
            },

            {
              test: "P-1 Computer Science (EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/c31f6abf1bcd9ba945960e75b94ffa9e"
            },

            //11
            {
              test: "P-2 Computer Science (AQA)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/fe1ba4f67d696d13df637161139c23c7"
            },

            {
              test: "P-2 Computer Science (OCR)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/c67a778473ecbf6335f780f195f29e9b"
            },

            {
              test: "P-2 Computer Science (EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/9dd6acc6b8963ef42be3dd5a8aa7edf6"
            },
          ],
        },

         {
          title: "Year 10 GCSE Mock Tests – Ilford",
          rows: [
            

            //7
            {
              test: "P-1 Chemistry (AQA)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/dd28f4a74eb165645807546ac1f638bb"
            },

            {
              test: "P-1 Chemistry (OCR)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/e6c48e4e23d47b6d24099cc96ad07942"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/956b7314f4b7f992f00410ffdcaed2af"
            },

            //8
            {
              test: "P-2 Chemistry (AQA)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/8cdbf5f7db3b16a662b7bfed694400fc"
            },

            {
              test: "P-2 Chemistry (OCR)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/ce6ef22b1755f85bca0b67fc940fa799"
            },

            {
              test: "P-2 Chemistry (EDEXCEL)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/b679798031709c83023c46e72080766c"
            },
           
            
            //9
            {
              test: "P-2 Biology (AQA)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/8b3be800b1d04e388cfd0066330c79e3"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/e4cd9f711afcf24c396ba8913c3a0271"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/57e9ab291b6d30100b1cb923c68084ca"
            },


            //10
            {
              test: "P-1 Computer Science (AQA)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/d624b82d3e6689b58f8e495e065f0661"
            },

            {
              test: "P-1 Computer Science (OCR)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/9ee088c10dcd84a18d482c6de6179305"
            },

            {
              test: "P-1 Computer Science (EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/e12f4713ac1ddbe5a6c67ec9ff8f7aee"
            },

            //11
            {
              test: "P-2 Computer Science (AQA)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/b8380e4c66b707a8461fb85f4c8940dd"
            },

            {
              test: "P-2 Computer Science (OCR)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/235a0fbea0b2fb9c024e4a7b9c947ff6"
            },

            {
              test: "P-2 Computer Science (EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/98b96c8ae1d5764694a90daa093dc1ab"
            },
          ],
        },
      ],

      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2479.123!2d0.0878!3d51.5590!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sIlford%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //Basildon

    {
      id: "essex-basildon-gcse-mocks",
      town: "Basildon",
      address: "Basildon St Paul's Church SS15 5HL",
      postcode: "SS15 5HL",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image: "https://l5.tm-web-02.co.uk/lib/image-large-wp2-697855.webp",
      badge: "New",
      distance: 7.8,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "The Basildon exam centre is easily accessible and thoughtfully designed to promote concentration and confidence. We offer a supportive atmosphere where students of all ability levels can perform at their best.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 7:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/romford-1.jpg",
        "assets/images/romford-2.jpg",
        "assets/images/romford-3.jpg",
        "assets/images/romford-4.jpg",
      ],
      
      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "Year 11 GCSE Mock Tests – Basildon",
          rows: [


            //1

            {
              test: "P-1 Chemistry (AQA)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/870f4fbe825d1452ae20fad572d71b01"
            },


            {
              test: "P-1 Chemistry (OCR)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/27b1b9ab5bc41fc528b01fb9aaccf31a"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/5219d722127e43a8321e0947655f7c83"
            },

            //2

            {
              test: "P-2 Chemistry  (AQA)",
              date: "Sunday, March 22, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/0762777ef641d68f208ad14a1df744a0"
            },

            {
              test: "P-2 Chemistry  (OCR)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/0899b9077394aa91d8c3bb2f30e77086"
            },

            {
              test: "P-2 Chemistry  (EDEXCEL)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/7cdfc3e137c7dc1980e7f253d4bdd2d1"
            },


            // 3
          

            {
              test: "P-2 Biology (AQA)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/a23227d094fd14fd7313bf28a7761b81"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/71657f946cbf40032272e05d0958d68b"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/443d52c3ae3a05b4bf48f8dca7a93f8e"
            },

            //4

            {
              test: "P-1 Computer Science (AQA)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/eedebe57d059003257b3e53e71560f49"
            },

            {
              test: "P-1 Computer Science (OCR)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/00b95235ea271fd706a6c9cdbc49ab1f"
            },

            {
              test: "P-1 Computer Science (EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/bf9eb0a70bcdc1a8b47d481420ff9d67"
            },

            //5
            {
              test: "P-2 Computer Science (AQA)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/ab54a884d45f3fc89885e439ea9716f2"
            },

            {
              test: "P-2 Computer Science (OCR)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/9c379fff9db832c177baeb54c2e78105"
            },

            {
              test: "P-2 Computer Science (EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/fe9105dec5532e5f144d3b5342b1d85f"
            },

          ],
        },
         {
          title: "Year 10 GCSE Mock Tests – Basildon",
          rows: [


            //1

            {
              test: "P-1 Chemistry (AQA)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/98b96c8ae1d5764694a90daa093dc1ab"
            },


            {
              test: "P-1 Chemistry (OCR)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/085422d3d6f832e73a09b972e979f2b3"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/a27f0fab94c9059bf1025ef6f36b6c68"
            },

            //2

            {
              test: "P-2 Chemistry  (AQA)",
              date: "Sunday, March 22, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/79ca64e62d43420264d0a0f7e710aa46"
            },

            {
              test: "P-2 Chemistry  (OCR)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/b92bedeac11fd3a328c7993f54d200c8"
            },

            {
              test: "P-2 Chemistry  (EDEXCEL)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/b92bedeac11fd3a328c7993f54d200c8"
            },


            // 3
          

            {
              test: "P-2 Biology (AQA)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/2eec20a24855697a0cae09839f66ea1e"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/f2dd6447bfa89c5dac73da6f87f4aa63"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/f2dd6447bfa89c5dac73da6f87f4aa63"
            },

            //4

            {
              test: "P-1 Computer Science (AQA)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/ffca949c7dda921dc47bf692b14d612f9"
            },

            {
              test: "P-1 Computer Science (OCR)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/adfd8e6da5d747e93d9b383d21ee51df"
            },

            {
              test: "P-1 Computer Science (EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/c11677b03808f090952e992db1d14c9b"
            },

            //5
            {
              test: "P-2 Computer Science (AQA)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/7a70ebe40f088cb2d51e1641d3b535a5"
            },

            {
              test: "P-2 Computer Science (OCR)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/09d25e4788ee000fc1bcdc9211526823"
            },

            {
              test: "P-2 Computer Science (EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/4fcda97381f6a15e71d73b8da5dcdb12"
            },

          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.456!2d0.1818!3d51.5762!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sRomford%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //South-end
    {
      id: "essex-southend-on-sea-gcse-mocks",
      town: "Southend-on-sea",
      address: "Thorpe Hall School, Wakering Rd, Southend-on-Sea ",
      postcode: "SS1 3RD",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://lh3.googleusercontent.com/p/AF1QipMZsUkgIylHxV-T7OALOq9JsFyYI4VLexJfeOBI=s1360-w1360-h1020",
      distance: 8.1,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/brentwood-1.jpg",
        "assets/images/brentwood-2.jpg",
        "assets/images/brentwood-3.jpg",
        "assets/images/brentwood-4.jpg",
      ],

      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "Year 11 GCSE Mock Tests – Southend-on-Sea",
          rows: [

            //1

            {
              test: "P-1 Chemistry (AQA)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/adfaed5ab654c4e0f9b40eed3f2383c6"
            },

            {
              test: "P-1 Chemistry (OCR)",
              date: "Sunday, March 15, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/d1efd009b461b74ac7c9ecd3e6f27c61"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/a73fd7cbe3811c4fd69729c85c7779b9"
            },

            //2

            {
              test: "P-2 Chemistry (AQA)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/9a13a6f3fda34082a68bef6073328ba4"
            },

            {
              test: "P-2 Chemistry (OCR)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/1cebdd2d1395eff7e748330bbce8f845"
            },

            {
              test: "P-2 Chemistry (EDEXCEL)",
              date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/9b82d24ec3ec09a8b6726f7e28e08446"
            },


            //3

            {
              test: "P-2 Biology (AQA)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/0ac31d74b1aff2d702f5852ef6e7221d"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/510ccac7a4e566ab86f2fbc4d3da8a75"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/92e8aeeaf818c7cb4614e16eb843e346"
            },


            //4

            {
              test: "P-1 Computer Science (AQA, OCR & EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/612ba1eb6eb08ad1d89e87c9d0b67106"
            },

            {
              test: "P-1 Computer Science (AQA, OCR & EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/023c4264a1c016b2012cb211656b5ed9"
            },

            {
              test: "P-1 Computer Science (AQA, OCR & EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/ae5f23e6843faa9e72bc0a95da335400"
            },

            // 5

            {
              test: "P-2 Computer Science (AQA, OCR & EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/31c7e14004211e6d26aecccf0b6886c1"
            },

            {
              test: "P-2 Computer Science (AQA, OCR & EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/31c7e14004211e6d26aecccf0b6886c1"
            },

            {
              test: "P-2 Computer Science (AQA, OCR & EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/675dc85985438ff5c98a0e1c882b5748"
            },
          ],
        },
        {
          title: "Year 10 GCSE Mock Tests – Southend-on-Sea",
          rows: [

            //1

            {
              test: "P-1 Chemistry (AQA)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/b32acab6cd0cc37cdd7f36b6e329009f"
            },

            {
              test: "P-1 Chemistry (OCR)",
              date: "Sunday, March 15, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/7e3392dc0b3705f23a34546ac6da629c"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Sunday, March 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/df8baa58969fad50f0ead4022d447b3c"
            },

            //2

            {
              test: "P-2 Chemistry (AQA)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/875f1fe0034eb28d3110d76e82e74cc8"
            },

            {
              test: "P-2 Chemistry (OCR)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/7e3392dc0b3705f23a34546ac6da629c"
            },

            {
              test: "P-2 Chemistry (EDEXCEL)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/19d4ab06b6aa3cd7d45a6f3b5fa57953"
            },


            //3

            {
              test: "P-2 Biology (AQA)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/da45a6dd6275d444eb1039a0b4de75f4"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/f539756747b63d8da49706771740aa0b"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Sunday, April 5, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/3223b617254117e5b13c6dab5e20260e"
            },


            //4

            {
              test: "P-1 Computer Science (AQA)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/da45a6dd6275d444eb1039a0b4de75f4"
            },

            {
              test: "P-1 Computer Science (OCR)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/b95b4f1e15c81804e5ec9a8f9e8e46d6"
            },

            {
              test: "P-1 Computer Science (EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/d27cea1d0c69c4a4f6db3af89a9124f2"
            },

            // 5

            {
              test: "P-2 Computer Science (AQA)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/008b4d703151d9cadb2ef0e043bdb6fe"
            },

            {
              test: "P-2 Computer Science (OCR)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/31c7e14004211e6d26aecccf0b6886c1"
            },

            {
              test: "P-2 Computer Science (EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/469d78704997e69c67772b73b110065e"
            },
          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

//     //Bexley

//     {
//       id: "kent-bexley-gcse-mocks",
//       town: "Bexleyheath",
//       address: " ",
//       postcode: "SS1 3RD",
//       phone: "(0)2039808244",
//       email: "support@sehyog.co.uk",
//       image:
//         "https://images.squarespace-cdn.com/content/v1/56520f11e4b02e2458a7dff8/890ce7a4-4101-433e-ade1-78b975d07d61/Beths-Grammar-School-11-plus-Exams-London%2B.jpg",
//       distance: 8.1,
//       services: ["11+ Mock Exams", "GCSE Mock Exams"],
//       description:
//         "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
//       openingHours: [
//         { day: "Monday", time: "9:00 AM - 7:00 PM" },
//         { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
//         { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
//         { day: "Thursday", time: "9:00 AM - 7:00 PM" },
//         { day: "Friday", time: "9:00 AM - 6:00 PM" },
//         { day: "Saturday", time: "9:00 AM - 7:00 PM" },
//         { day: "Sunday", time: "9:00 AM - 7:00 PM" },
//       ],
//       photos: [
//         "assets/images/brentwood-1.jpg",
//         "assets/images/brentwood-2.jpg",
//         "assets/images/brentwood-3.jpg",
//         "assets/images/brentwood-4.jpg",
//       ],
//       faqs: [
//         {
//           question: "Is there parking available?",
//           answer: "Yes, ample parking available at the center.",
//         },
//         {
//           question: "What subjects are covered in the mock exams?",
//           answer:
//             "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
//         },
//         {
//           question: "Are the mock exams based on real exam formats?",
//           answer:
//             "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
//         },
//         {
//           question: "Who writes the mock exam papers?",
//           answer:
//             "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
//         },
//         {
//           question: "Are the mocks suitable for all ability levels?",
//           answer:
//             "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
//         },
//         {
//           question: "How do I book a mock exam?",
//           answer:
//             "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
//         },
//         {
//           question: "Can I choose a specific exam centre?",
//           answer:
//             "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
//         },
//         {
//           question: "What is included in the mock exam fee?",
//           answer:
//             "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
//         },
//         {
//           question: "Are refunds available if my child cannot attend?",
//           answer:
//             "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
//         },
//         {
//           question: "What should students bring on the exam day?",
//           answer:
//             "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
//         },
//         {
//           question: "How closely do you follow real exam conditions?",
//           answer:
//             "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
//         },
//         {
//           question: "How long do the exams last?",
//           answer:
//             "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
//         },
//         {
//           question: "When will results be available?",
//           answer:
//             "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
//         },
//         {
//           question: "What feedback will we receive?",
//           answer:
//             "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
//         },
//         {
//           question: "Will rankings or comparisons be provided?",
//           answer:
//             "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
//         },
//         {
//           question:
//             "Can the mock results be used to guide further preparation?",
//           answer:
//             "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
//         },
//         {
//           question: "Do you offer follow-up support after the mocks?",
//           answer:
//             "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
//         },
//       ],

//       schedules: [
//         {
//   title: "11+ GL Kent Mock Tests – Bexleyheath",
//   rows: [
//     { test: "GL Kent 1st SET", date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/b75c304730c55341bae19b5d9f2c9ca8" },
//     { test: "GL Kent 2nd", date: "Sunday, May 03, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/8d89bb42a8db4b1bda692f9c6731d90f" },
//     { test: "GL Kent 3rd", date: "Sunday, May 31, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/7b116dfa739ebd142d814eb8830fd5f9" },
//     { test: "GL Kent 4th", date: "Sunday, June 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/b88941b24ee078884a2115d4764b8dd5" },
//     { test: "GL Kent 5th", date: "Sunday, July 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/d719b80d8f1fa84f93c0cb4bcb72ca12" },
//     { test: "GL Kent 6th", date: "Sunday, August 02, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/235d15b4f65fb1971b4c7bce48bc13d7" },
//     { test: "GL Kent 7th", date: "Sunday, August 23, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/6ef9d4923773176ea8cb5862290eb205" },
//   ]
// },

// {
//   title: "11+ GL Bexley Mock Tests – Bexleyheath",
//   rows: [
//     { test: "GL Bexley 1st SET", date: "Sunday, March 22, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/05d66cf3937f1b38c8c4da7258737135" },
//     { test: "GL Bexley 2nd", date: "Sunday, April 26, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/d1b234a7d1dc20f6993a905a86caf2e6" },
//     { test: "GL Bexley 3rd", date: "Sunday, May 17, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/0c2e40d7266d4b00c6a3e6b58ac073e5" },
//     { test: "GL Bexley 4th", date: "Sunday, June 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a25bb410c31bc2f1a8404a9192dad0f2" },
//     { test: "GL Bexley 5th", date: "Sunday, July 05, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/11137da0bc68093d89620ab8f48ae221" },
//     { test: "GL Bexley 6th", date: "Sunday, July 26, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/bea360a4b2780570c3292f5399bbd578" },
//     { test: "GL Bexley 7th", date: "Sunday, August 16, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/972c631a5458b1a83b32bf1bcf163ce7" },
//   ]
// },
//       ],
//       mapUrl:
//         "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
//     },

    //Amersham

    {
      id: "bucks-amersham-gcse-mocks",
      town: "Amersham",
      address: " ",
      postcode: "SS1 3RD",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://school-space-documents.s3.amazonaws.com/IMG_1098.JPG",
      distance: 8.1,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/brentwood-1.jpg",
        "assets/images/brentwood-2.jpg",
        "assets/images/brentwood-3.jpg",
        "assets/images/brentwood-4.jpg",
      ],

      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      // Y11 MOCK TEST

      schedules: [
        {
          title: "Year 11 GCSE Mock Tests – Amersham",
          rows: [

            //1

            {
              test: "P-1 Chemistry (AQA)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/7319b7ee5de5eec33604304ea447f78b"
            },

            {
              test: "P-1 Chemistry (OCR)",
              date: "Saturday, March 14, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/f3868868148777afc9a383c1a7dcd4bd"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/70746272b031c4e3ec0a9cdd308d993d"
            },

            //2

            {
              test: "P-2 Chemistry (AQA)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/70746272b031c4e3ec0a9cdd308d993d"
            },

            {
              test: "P-2 Chemistry (OCR)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/d22279a49ebbded2a9af269f0939a567"
            },

            {
              test: "P-2 Chemistry (EDEXCEL)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/4a2ab03175fac68ccf980cb8d6e9e67b"
            },


            //3

            {
              test: "P-2 Biology (AQA)",
              date: "Saturday, April 4, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/1c94f47de3991374368674a3ec3c9ea6"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Saturday, April 4, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/60e647d1bd8a2519a3245fb46ac0e051"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Saturday, April 4, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/f704a3daf06e27e0b0484b2676f26697"
            },


            //4

            {
              test: "P-1 Computer Science (AQA)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/0fa9e24d71da4acca504f840a22636b2"
            },

            {
              test: "P-1 Computer Science (OCR)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/0fa9e24d71da4acca504f840a22636b2"
            },

            {
              test: "P-1 Computer Science (EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/38199cdc6d4c150dfb2fc00592290f8b"
            },

            // 5

            {
              test: "P-2 Computer Science (AQA)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/40be5f4c1f1ea60605d23b0b2d20ece7"
            },

            {
              test: "P-2 Computer Science (OCR)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/40be5f4c1f1ea60605d23b0b2d20ece7"
            },

            {
              test: "P-2 Computer Science (EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/gcse-mock-test/0982404eb27704b95ac3725162726cfb"
            },
          ],
        },


        //Y10 mock test
        {
          title: "Year 10 GCSE Mock Tests – Amersham",
          rows: [

            //1

            {
              test: "P-1 Chemistry (AQA)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/dc5883cfcd7731f0422edb87ba118e86"
            },

            {
              test: "P-1 Chemistry (OCR)",
              date: "Saturday, March 14, 2026",  link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/dc5883cfcd7731f0422edb87ba118e86"
            },

            {
              test: "P-1 Chemistry (EDEXCEL)",
              date: "Saturday, March 14, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/df8baa58969fad50f0ead4022d447b3c"
            },

            //2

            {
              test: "P-2 Chemistry (AQA)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/ab1c78f72da76d7da2c52c2045636eb7"
            },

            {
              test: "P-2 Chemistry (OCR)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/89e8689b7e2ed7c200c8e41fc39a159b"
            },

            {
              test: "P-2 Chemistry (EDEXCEL)",
              date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/a97d3cad1f111725b3f8d75783c6efba"
            },


            //3

            {
              test: "P-2 Biology (AQA)",
              date: "Saturday, April 4, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/d03eeb04769bc2ba0ebf24855023d119"
            },

            {
              test: "P-2 Biology (OCR)",
              date: "Saturday, April 4, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/10f89fb849f3897bf9d3b688043b5024"
            },

            {
              test: "P-2 Biology (EDEXCEL)",
              date: "Saturday, April 4, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/f2326d9def6d4afe73e79d79d4c6ce75"
            },


            //4

            {
              test: "P-1 Computer Science (AQA)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/dc5883cfcd7731f0422edb87ba118e86"
            },

            {
              test: "P-1 Computer Science (OCR)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/92852f4625a952ac55a47c1ec1cb4c1c"
            },

            {
              test: "P-1 Computer Science (EDEXCEL)",
              date: "Sunday, April 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/b7c8df14e84245a7e0d66a88832f87a1"
            },

            // 5

            {
              test: "P-2 Computer Science (AQA)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/04dbff7c79b7d7e5723c139ff5aea54f"
            },

            {
              test: "P-2 Computer Science (OCR)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/1c90b3af381da5af6854fe31d0ecd99c"
            },

            {
              test: "P-2 Computer Science (EDEXCEL)",
              date: "Sunday, April 19, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/y10_gcse_mock_test/0682412580379bb59370eb7ff8b73a68"
            },
          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

//     //Milton Keynes

//     {
//       id: "bucks-milton-keynes-gcse-mocks",
//       town: "Milton Keynes",
//       address: " ",
//       postcode: "SS1 3RD",
//       phone: "(0)2039808244",
//       email: "support@sehyog.co.uk",
//       image:
//         "https://upload.wikimedia.org/wikipedia/commons/0/07/St_Pauls_School_Milton_Keynes.jpg",
//       distance: 8.1,
//       services: ["11+ Mock Exams", "GCSE Mock Exams"],
//       description:
//         "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
//       openingHours: [
//         { day: "Monday", time: "9:00 AM - 7:00 PM" },
//         { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
//         { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
//         { day: "Thursday", time: "9:00 AM - 7:00 PM" },
//         { day: "Friday", time: "9:00 AM - 6:00 PM" },
//         { day: "Saturday", time: "9:00 AM - 7:00 PM" },
//         { day: "Sunday", time: "9:00 AM - 7:00 PM" },
//       ],
//       photos: [
//         "assets/images/brentwood-1.jpg",
//         "assets/images/brentwood-2.jpg",
//         "assets/images/brentwood-3.jpg",
//         "assets/images/brentwood-4.jpg",
//       ],

//       faqs: [
//         {
//           question: "Is there parking available?",
//           answer: "Yes, ample parking available at the center.",
//         },
//         {
//           question: "What subjects are covered in the mock exams?",
//           answer:
//             "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
//         },
//         {
//           question: "Are the mock exams based on real exam formats?",
//           answer:
//             "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
//         },
//         {
//           question: "Who writes the mock exam papers?",
//           answer:
//             "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
//         },
//         {
//           question: "Are the mocks suitable for all ability levels?",
//           answer:
//             "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
//         },
//         {
//           question: "How do I book a mock exam?",
//           answer:
//             "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
//         },
//         {
//           question: "Can I choose a specific exam centre?",
//           answer:
//             "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
//         },
//         {
//           question: "What is included in the mock exam fee?",
//           answer:
//             "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
//         },
//         {
//           question: "Are refunds available if my child cannot attend?",
//           answer:
//             "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
//         },
//         {
//           question: "What should students bring on the exam day?",
//           answer:
//             "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
//         },
//         {
//           question: "How closely do you follow real exam conditions?",
//           answer:
//             "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
//         },
//         {
//           question: "How long do the exams last?",
//           answer:
//             "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
//         },
//         {
//           question: "When will results be available?",
//           answer:
//             "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
//         },
//         {
//           question: "What feedback will we receive?",
//           answer:
//             "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
//         },
//         {
//           question: "Will rankings or comparisons be provided?",
//           answer:
//             "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
//         },
//         {
//           question:
//             "Can the mock results be used to guide further preparation?",
//           answer:
//             "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
//         },
//         {
//           question: "Do you offer follow-up support after the mocks?",
//           answer:
//             "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
//         },
//       ],

//       schedules: [
//          {
//   title: "11+ Mock Tests – Amersham",
//   rows: [
//     { test: "GL Bucks 1st SET", date: "Saturday, March 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/c359636be1c830e6042a9fa0b4db633f" },
//     { test: "GL Bucks 2nd SET", date: "Saturday, April 25, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/ac71388632f172cb1d6075eaceca2fa7" },
//     { test: "GL Bucks 3rd SET", date: "Saturday, May 16, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/6112a8375c338d53d65874af0527d6df" },
//     { test: "GL Bucks 4th SET", date: "Saturday, June 13, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/664c9e918cd16d17acca40840669b77b" },
//     { test: "GL Bucks 5th SET", date: "Saturday, July 03, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/c12796bf12b1056ea39d72f23b1a03ca" },
//     { test: "GL Bucks 6th SET", date: "Saturday, July 25, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/f40e0f6b3e03a65eeca966030aa8649b" },
//     { test: "GL Bucks 7th SET", date: "Saturday, August 15, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/7d1d9698c737bf4ad94505eafce0a132" }
//   ]
// }
//       ],
//       mapUrl:
//         "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
//     },

//      //Harrow

//     {
//       id: "harrow-gcse-mocks",
//       town: "Harrow",
//       address: " ",
//       postcode: "SS1 3RD",
//       phone: "(0)2039808244",
//       email: "support@sehyog.co.uk",
//       image:
//         "https://www.academic-asia.com/_next/image?url=https%3A%2F%2Facademic-asia.s3.ap-east-1.amazonaws.com%2Fcentral_22_without_roadworks2_2_04525644b2.jpg&w=3840&q=100",
//       distance: 8.1,
//       services: ["11+ Mock Exams", "GCSE Mock Exams"],
//       description:
//         "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
//       openingHours: [
//         { day: "Monday", time: "9:00 AM - 7:00 PM" },
//         { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
//         { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
//         { day: "Thursday", time: "9:00 AM - 7:00 PM" },
//         { day: "Friday", time: "9:00 AM - 6:00 PM" },
//         { day: "Saturday", time: "9:00 AM - 7:00 PM" },
//         { day: "Sunday", time: "9:00 AM - 7:00 PM" },
//       ],
//       photos: [
//         "assets/images/brentwood-1.jpg",
//         "assets/images/brentwood-2.jpg",
//         "assets/images/brentwood-3.jpg",
//         "assets/images/brentwood-4.jpg",
//       ],

//       faqs: [
//         {
//           question: "Is there parking available?",
//           answer: "Yes, ample parking available at the center.",
//         },
//         {
//           question: "What subjects are covered in the mock exams?",
//           answer:
//             "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
//         },
//         {
//           question: "Are the mock exams based on real exam formats?",
//           answer:
//             "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
//         },
//         {
//           question: "Who writes the mock exam papers?",
//           answer:
//             "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
//         },
//         {
//           question: "Are the mocks suitable for all ability levels?",
//           answer:
//             "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
//         },
//         {
//           question: "How do I book a mock exam?",
//           answer:
//             "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
//         },
//         {
//           question: "Can I choose a specific exam centre?",
//           answer:
//             "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
//         },
//         {
//           question: "What is included in the mock exam fee?",
//           answer:
//             "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
//         },
//         {
//           question: "Are refunds available if my child cannot attend?",
//           answer:
//             "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
//         },
//         {
//           question: "What should students bring on the exam day?",
//           answer:
//             "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
//         },
//         {
//           question: "How closely do you follow real exam conditions?",
//           answer:
//             "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
//         },
//         {
//           question: "How long do the exams last?",
//           answer:
//             "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
//         },
//         {
//           question: "When will results be available?",
//           answer:
//             "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
//         },
//         {
//           question: "What feedback will we receive?",
//           answer:
//             "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
//         },
//         {
//           question: "Will rankings or comparisons be provided?",
//           answer:
//             "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
//         },
//         {
//           question:
//             "Can the mock results be used to guide further preparation?",
//           answer:
//             "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
//         },
//         {
//           question: "Do you offer follow-up support after the mocks?",
//           answer:
//             "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
//         },
//       ],

//       schedules: [
//         {
//   title: "11+ GL QE Boys Mock Tests – Harrow",
//   rows: [
//     { test: "GL QE Boys 1st SET", date: "Sunday, March 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/4b6d66128f00532dcfbc7680b6545d41" },
//     { test: "GL QE Boys 2nd SET", date: "Sunday, April 03, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/f94c17ed712de72e3936df97c9a43075" },
//     { test: "GL QE Boys 3rd SET", date: "Sunday, April 31, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/9e201c2cbfe3b627579096e6d15ab91b" },
//     { test: "GL QE Boys 4th SET", date: "Sunday, May 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e4d647aad1b83b80c0053d3e9df0d96e" },
//     { test: "GL QE Boys 5th SET", date: "Sunday, June 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/d98722f59e0da089a7b46a5f05582a25" },
//     { test: "GL QE Boys 6th SET", date: "Sunday, July 02, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e301444ef3744d3166a89cff3efa8325" },
//     { test: "GL QE Boys 7th SET", date: "Sunday, July 23, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/80a21ef8f45961e0caba1363eca7be89" },
//   ]
// },

// {
//   title: "11+ GL HBS Girls Mock Tests – Harrow",
//   rows: [
//     { test: "GL HBS Girls 1st SET", date: "Sunday, March 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/3b2e41c8a5c2912baf0d1c31bbca6b1f" },
//     { test: "GL HBS Girls 2nd SET", date: "Sunday, April 03, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/80a60c4a218a10a1fc98f22b8c0525d6" },
//     { test: "GL HBS Girls 3rd SET", date: "Sunday, April 31, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/f3d0ad93c3c01957444b36e0293c928b" },
//     { test: "GL HBS Girls 4th SET", date: "Sunday, May 21, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/686a455bb023ad285268c81f2ff798c0" },
//     { test: "GL HBS Girls 5th SET", date: "Sunday, June 12, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/f860f43ffd69bd6f51c686d9482c2aa7" },
//     { test: "GL HBS Girls 6th SET", date: "Sunday, July 02, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/6d71f628b99a092dba24820827d1e64d" },
//     { test: "GL HBS Girls 7th SET", date: "Sunday, July 23, 2026", link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/9798c7a68e5c84cbcb7d90bf86d0fb65" }
//   ]
// },
//       ],
//       mapUrl:
//         "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
//     },


    //ONLINE

    {
      id: "online-gcse-mocks",
      town: "Online",
      address: "Online",
      postcode: "--",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://cdn2.exam.net/website-2025-11-05/wp-content/uploads/2024/03/examnet-student-doing-homework.png",
      distance: 0.0,
      services: [
        "11+ Mock Exams",
        "GCSE Mock Exams",
        "SAT Mock Exam",
        "Online",
      ],
      description:
        "Our Online Exams are available to book and attenend from home at any point of time",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/brentwood-1.jpg",
        "assets/images/brentwood-2.jpg",
        "assets/images/brentwood-3.jpg",
        "assets/images/brentwood-4.jpg",
      ],

      faqs: [
        {
          question: "Where to book Online Tests?",
          answer: "Visit the Mock Test Calender and Book.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "GCSE Mock Tests – Online",
          rows: [
            { test: "P-1 Maths (EDEXCEL)", date: "Sunday, January 11, 2026" },
            { test: "P-2 Maths (EDEXCEL)", date: "Sunday, January 18, 2026" },
            { test: "P-3 Maths (EDEXCEL)", date: "Sunday, January 25, 2026" },

            {
              test: "P-1 Literature (AQA, OCR & EDEXCEL)",
              date: "Sunday, February 1, 2026",
            },
            {
              test: "P-1 Language (AQA, OCR & EDEXCEL)",
              date: "Sunday, February 8, 2026",
            },
            {
              test: "P-2 Literature (AQA, OCR & EDEXCEL)",
              date: "Sunday, February 15, 2026",
            },
            {
              test: "P-2 Language (AQA, OCR & EDEXCEL)",
              date: "Sunday, February 22, 2026",
            },

            {
              test: "Physics P-1 (AQA, OCR & EDEXCEL)",
              date: "Sunday, March 1, 2026",
            },
            {
              test: "Physics P-2 (AQA, OCR & EDEXCEL)",
              date: "Sunday, March 8, 2026",
            },
            {
              test: "Chemistry P-1 (AQA, OCR & EDEXCEL)",
              date: "Sunday, March 15, 2026",
            },
            {
              test: "Chemistry P-2 (AQA, OCR & EDEXCEL)",
              date: "Sunday, March 22, 2026",
            },
            {
              test: "Biology P-1 (AQA, OCR & EDEXCEL)",
              date: "Sunday, March 29, 2026",
            },

            {
              test: "Biology P-2 (AQA, OCR & EDEXCEL)",
              date: "Sunday, April 5, 2026",
            },
            {
              test: "Computer Science P-1 (AQA, OCR & EDEXCEL)",
              date: "Sunday, April 12, 2026",
            },
            {
              test: "Computer Science P-2 (AQA, OCR & EDEXCEL)",
              date: "Sunday, April 19, 2026",
            },
          ],
        },
      ],

      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },
  ];

  constructor() {}

  getAllLocations(): TutionLocation[] {
    return this.locations;
  }

  getLocationByTown(town: string): TutionLocation {
    return this.locations.find((loc) => loc.id === town.toLowerCase());
  }

  searchLocations(searchTerm: string): TutionLocation[] {
    if (!searchTerm) {
      return this.locations;
    }
    searchTerm = searchTerm.toLowerCase();
    return this.locations.filter(
      (loc) =>
        loc.town.toLowerCase().includes(searchTerm) ||
        loc.address.toLowerCase().includes(searchTerm) ||
        loc.postcode.toLowerCase().includes(searchTerm)
    );
  }

  filterByService(service: string): TutionLocation[] {
    if (service === "all") {
      return this.locations;
    }
    return this.locations.filter((loc) =>
      loc.services.some((s) => s.toLowerCase().includes(service.toLowerCase()))
    );
  }
}
